import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import { FiPlus, FiSave, FiSearch, FiTrash2, FiX } from 'react-icons/fi';
import { API_URLS, getPlayerImageUrl, getTeamImageUrl, getTournamentImageUrl } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import {
  authorizedPost,
  fileToBase64,
  getApiErrorMessage,
  getApiErrorPayload,
} from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { useAdminAccess } from '../utilities/adminAccess';
import {
  champFields,
  coachBirthdayValue,
  coachDisplayName,
  coachRole,
  countryFields,
  extractCoaches,
  extractEntity,
  extractApplications,
  extractList,
  extractTeamDoubles,
  extractTeamLeagues,
  formatTeamDoubleConflicts,
  playerDisplayName,
  seasonRosterTeamIds,
  teamCountryId,
  teamShowEnabled,
  toDateInputValue,
} from '../utilities/tournamentsApi';
import { isPlayerProfessional, playerCardPath, playerNewPath } from '../utilities/playersApi';
import AdminPhotoUpload from '../components/AdminPhotoUpload';
import AdminSearchPicker from '../components/AdminSearchPicker';
import CustomModal from '../components/CustomModal';
import ReorderableTable from '../components/ReorderableTable';
import StadiumCrumbs from '../components/StadiumCrumbs';
import defaultPlayerImage from '../images/defaultAvatar.png';
import defaultTeamLogo from '../images/defaultTeamLogo.png';
import '../styles/createMatchModal.css';
import '../styles/stadiumsManagement.css';
import '../styles/disqualificationsManagement.css';
import '../styles/tournamentsManagement.css';
import '../styles/applicationsManagement.css';
import '../styles/newsManagement.css';

const defaultLeagueImage = () => getTournamentImageUrl('/images/others/no_photo.png');

const emptyForm = {
  enabled: true,
  name: '',
  short_name: '',
  country_id: '',
};

export default function ManageTeam() {
  const { teamId } = useParams();
  const isNew = !teamId;
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useI18n();
  const { champId, loading: champsLoading } = useChampionships();
  const { canWrite } = useAdminAccess('teams');
  const { canWrite: canWritePlayers } = useAdminAccess('players');

  const [form, setForm] = useState(emptyForm);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [photoError, setPhotoError] = useState('');
  const [errors, setErrors] = useState({});
  const [countries, setCountries] = useState([]);
  const [applications, setApplications] = useState([]);
  const [selectedApplicationId, setSelectedApplicationId] = useState('');
  const [playerQuery, setPlayerQuery] = useState('');
  const [playerSort, setPlayerSort] = useState({ key: 'name', dir: 'asc' });
  const [coaches, setCoaches] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addingLeague, setAddingLeague] = useState(false);
  const [removingLeague, setRemovingLeague] = useState(false);
  const [addLeagueVisible, setAddLeagueVisible] = useState(false);
  const [addLeagueLoading, setAddLeagueLoading] = useState(false);
  const [addCountryId, setAddCountryId] = useState('');
  const [addLeagueId, setAddLeagueId] = useState('');
  const [addSeasonId, setAddSeasonId] = useState('');
  const [addCountryLeagues, setAddCountryLeagues] = useState([]);
  const [addSeasons, setAddSeasons] = useState([]);
  const [addLeagueError, setAddLeagueError] = useState('');
  const [leagueToRemove, setLeagueToRemove] = useState(null);
  const [playerToRemove, setPlayerToRemove] = useState(null);
  const [removingPlayer, setRemovingPlayer] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [doubles, setDoubles] = useState({
    parent: null,
    reserve: null,
    chain: [],
  });
  const [doublesBusy, setDoublesBusy] = useState(false);
  const [doubleReplace, setDoubleReplace] = useState(null);

  useEffect(() => {
    if (!champId && champsLoading) return;
    initPage();
  }, [teamId, champId, champsLoading]);

  const applyDoubles = (payload) => {
    const packed = extractTeamDoubles(payload);
    setDoubles({
      parent: packed.parent,
      reserve: packed.reserve,
      chain: packed.chain,
    });
  };

  const initPage = async () => {
    setMessage({ text: '', type: '' });
    setErrors({});
    setPhotoFile(null);
    setPhotoError('');
    setDoubleReplace(null);

    if (!champId) {
      setLoading(false);
      setMessage({ text: t('championshipsLoadError'), type: 'error' });
      return;
    }

    setLoading(true);
    try {
      const countriesResponse = await authorizedPost(dispatch, API_URLS.getAdminCountries, champFields(champId));
      const countryOptions = extractList(countriesResponse.data);
      setCountries(countryOptions);

      if (isNew) {
        setForm({
          ...emptyForm,
          country_id: countryOptions[0] ? String(countryOptions[0].id) : '',
        });
        setPhotoPreview('');
        setApplications([]);
        setSelectedApplicationId('');
        setCoaches([]);
        setLeagues([]);
        setDoubles({ parent: null, reserve: null, chain: [] });
        return;
      }

      const [teamResponse, appsResponse] = await Promise.all([
        authorizedPost(dispatch, API_URLS.getCatalogTeam, {
          id: Number(teamId),
        }),
        authorizedPost(dispatch, API_URLS.getAdminTeamApplications, {
          id: Number(teamId),
        }).catch(() => ({ data: { applications: [] } })),
      ]);

      const team = extractEntity(teamResponse.data);
      setForm({
        enabled: teamShowEnabled(team),
        name: team.name || '',
        short_name: team.short_name || '',
        country_id: teamCountryId(team) ? String(teamCountryId(team)) : '',
      });
      const imageUrl = getTeamImageUrl(team.image);
      setPhotoPreview(imageUrl ? `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}v=${Date.now()}` : '');
      setCoaches(extractCoaches(team));
      setLeagues(extractTeamLeagues(team));
      applyDoubles(team);
      const apps = extractApplications(appsResponse.data)
        .slice()
        .sort((a, b) => (Number(a.priority) || 0) - (Number(b.priority) || 0));
      setApplications(apps);
      setSelectedApplicationId(apps[0] ? String(apps[0].application_id || apps[0].id || '') : '');
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('stLoadError')), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const reloadTeamDoubles = async () => {
    const teamResponse = await authorizedPost(dispatch, API_URLS.getCatalogTeam, {
      id: Number(teamId),
    });
    applyDoubles(extractEntity(teamResponse.data));
  };

  const linkTeamDouble = async ({ mainId, doubleId, replace = false }) => {
    if (!canWrite || doublesBusy) return;
    setDoublesBusy(true);
    setMessage({ text: '', type: '' });
    try {
      await authorizedPost(dispatch, API_URLS.setAdminTeamDouble, {
        main_team_id: Number(mainId),
        double_team_id: Number(doubleId),
        ...(replace ? { replace: true } : {}),
      });
      await reloadTeamDoubles();
      setDoubleReplace(null);
      setMessage({ text: t('tmDoubleLinked'), type: 'success' });
    } catch (error) {
      const payload = getApiErrorPayload(error);
      if (error?.response?.status === 409 && !replace) {
        setDoubleReplace({
          mainId: Number(mainId),
          doubleId: Number(doubleId),
          conflictsText: formatTeamDoubleConflicts(payload?.conflicts, t),
        });
      } else {
        setMessage({
          text: getApiErrorMessage(error, t('tmDoubleLinkError')),
          type: 'error',
        });
      }
    } finally {
      setDoublesBusy(false);
    }
  };

  const unlinkTeamDouble = async (role = 'auto') => {
    if (!canWrite || doublesBusy || isNew) return;
    let body = { team_id: Number(teamId) };
    if (role === 'parent' && doubles.parent?.id) {
      body = {
        main_team_id: Number(doubles.parent.id),
        double_team_id: Number(teamId),
      };
    } else if (role === 'reserve' && doubles.reserve?.id) {
      body = {
        main_team_id: Number(teamId),
        double_team_id: Number(doubles.reserve.id),
      };
    }
    setDoublesBusy(true);
    setMessage({ text: '', type: '' });
    try {
      await authorizedPost(dispatch, API_URLS.deleteAdminTeamDouble, body);
      await reloadTeamDoubles();
      setMessage({ text: t('tmDoubleUnlinked'), type: 'success' });
    } catch (error) {
      setMessage({
        text: getApiErrorMessage(error, t('tmDoubleUnlinkError')),
        type: 'error',
      });
    } finally {
      setDoublesBusy(false);
    }
  };

  const onPickParent = (item) => {
    const parentId = Number(item?.id);
    if (!parentId || parentId === Number(teamId)) return;
    linkTeamDouble({ mainId: parentId, doubleId: Number(teamId) });
  };

  const onPickReserve = (item) => {
    const reserveId = Number(item?.id);
    if (!reserveId || reserveId === Number(teamId)) return;
    linkTeamDouble({ mainId: Number(teamId), doubleId: reserveId });
  };

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: '' }));
    }
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = t('tmNameRequired');
    if (!form.country_id) nextErrors.country_id = t('tmCountryRequired');
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      const payload = {
        ...champFields(champId),
        ...countryFields(form.country_id),
        name: form.name.trim(),
        short_name: form.short_name.trim() || form.name.trim(),
        enabled: form.enabled ? 1 : 0,
      };
      if (photoFile) {
        payload.image = await fileToBase64(photoFile);
      }

      if (isNew) {
        const response = await authorizedPost(dispatch, API_URLS.addCatalogTeam, payload);
        const created = extractEntity(response.data);
        setMessage({ text: t('tmCreated'), type: 'success' });
        if (created.id || created.team_id) {
          history.replace(`/teams_management/${created.id || created.team_id}`);
        } else {
          history.push('/teams_management');
        }
      } else {
        await authorizedPost(dispatch, API_URLS.changeCatalogTeam, {
          ...payload,
          id: Number(teamId),
        });
        setPhotoFile(null);
        setMessage({ text: t('stSaved'), type: 'success' });
      }
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('stSaveError')), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const reloadTeamLeagues = async () => {
    const response = await authorizedPost(dispatch, API_URLS.getCatalogTeam, {
      id: Number(teamId),
    });
    setLeagues(extractTeamLeagues(extractEntity(response.data)));
  };

  const fetchSeasonTeamIds = async (leagueId, seasonId) => {
    const response = await authorizedPost(dispatch, API_URLS.getAdminSeasonTeams, {
      ...champFields(champId),
      league_id: Number(leagueId),
      season_id: Number(seasonId),
    });
    return seasonRosterTeamIds(response.data);
  };

  const saveSeasonTeamIds = async (leagueId, seasonId, teamIds) => {
    await authorizedPost(dispatch, API_URLS.setSeasonTeams, {
      ...champFields(champId),
      league_id: Number(leagueId),
      season_id: Number(seasonId),
      teams: teamIds,
    });
  };

  const isMissingRoute = (error) => {
    const status = error?.response?.status;
    return status === 404 || status === 405;
  };

  const applyLeaguesPayload = (data) => {
    const entity = extractEntity(data);
    const source = Array.isArray(entity.leagues)
      ? entity
      : (Array.isArray(data?.leagues) ? data : null);
    if (!source) return false;
    setLeagues(extractTeamLeagues(source));
    return true;
  };

  const teamLeaguePayload = (leagueId, seasonId) => ({
    ...champFields(champId),
    id: Number(teamId),
    team_id: Number(teamId),
    league_id: Number(leagueId),
    season_id: Number(seasonId),
  });

  const attachTeamToSeason = async (leagueId, seasonId) => {
    try {
      const response = await authorizedPost(dispatch, API_URLS.addTeamLeague, teamLeaguePayload(leagueId, seasonId));
      if (!applyLeaguesPayload(response.data)) await reloadTeamLeagues();
    } catch (error) {
      if (!isMissingRoute(error)) throw error;
      const currentIds = await fetchSeasonTeamIds(leagueId, seasonId);
      const id = Number(teamId);
      if (!currentIds.includes(id)) {
        await saveSeasonTeamIds(leagueId, seasonId, [...currentIds, id]);
      }
      await reloadTeamLeagues();
    }
  };

  const detachTeamFromSeason = async (leagueId, seasonId) => {
    try {
      const response = await authorizedPost(dispatch, API_URLS.deleteTeamLeague, teamLeaguePayload(leagueId, seasonId));
      if (!applyLeaguesPayload(response.data)) await reloadTeamLeagues();
    } catch (error) {
      if (!isMissingRoute(error)) throw error;
      const currentIds = await fetchSeasonTeamIds(leagueId, seasonId);
      await saveSeasonTeamIds(
        leagueId,
        seasonId,
        currentIds.filter((id) => id !== Number(teamId)),
      );
      await reloadTeamLeagues();
    }
  };

  const loadAddCountryLeagues = async (countryId) => {
    if (!countryId) {
      setAddCountryLeagues([]);
      return;
    }
    setAddLeagueLoading(true);
    setAddLeagueError('');
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminCountry, {
        id: Number(countryId),
      });
      setAddCountryLeagues(extractList(extractEntity(response.data).leagues));
    } catch (error) {
      setAddCountryLeagues([]);
      setAddLeagueError(getApiErrorMessage(error, t('atlLoadError')));
    } finally {
      setAddLeagueLoading(false);
    }
  };

  const loadAddSeasons = async (leagueId) => {
    if (!leagueId) {
      setAddSeasons([]);
      return;
    }
    setAddLeagueLoading(true);
    setAddLeagueError('');
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminLeague, {
        id: Number(leagueId),
      });
      setAddSeasons(extractList(extractEntity(response.data).seasons));
    } catch (error) {
      setAddSeasons([]);
      setAddLeagueError(getApiErrorMessage(error, t('atlSeasonsError')));
    } finally {
      setAddLeagueLoading(false);
    }
  };

  const resetAddLeagueForm = () => {
    setAddLeagueId('');
    setAddSeasonId('');
    setAddCountryLeagues([]);
    setAddSeasons([]);
    setAddLeagueError('');
  };

  const closeAddLeague = () => {
    if (addingLeague) return;
    setAddLeagueVisible(false);
    resetAddLeagueForm();
  };

  const openAddLeague = async () => {
    const countryId = form.country_id || (countries[0] ? String(countries[0].id) : '');
    setAddCountryId(countryId);
    setAddLeagueId('');
    setAddSeasonId('');
    setAddSeasons([]);
    setAddLeagueError('');
    setAddLeagueVisible(true);
    await loadAddCountryLeagues(countryId);
  };

  const handleAddCountryChange = async (countryId) => {
    setAddCountryId(countryId);
    setAddLeagueId('');
    setAddSeasonId('');
    setAddSeasons([]);
    await loadAddCountryLeagues(countryId);
  };

  const handleAddLeagueChange = async (leagueId) => {
    setAddLeagueId(leagueId);
    setAddSeasonId('');
    await loadAddSeasons(leagueId);
  };

  const handleAddLeague = async () => {
    if (!addLeagueId || !addSeasonId) {
      setAddLeagueError(!addLeagueId ? t('leaguePlaceholder') : t('stSeasonPlaceholder'));
      return;
    }
    setAddingLeague(true);
    setAddLeagueError('');
    try {
      await attachTeamToSeason(addLeagueId, addSeasonId);
      setAddLeagueVisible(false);
      resetAddLeagueForm();
      setMessage({ text: t('atlAdded'), type: 'success' });
    } catch (error) {
      setAddLeagueError(getApiErrorMessage(error, t('atlAddError')));
    } finally {
      setAddingLeague(false);
    }
  };

  const handleRemoveLeague = async () => {
    if (!leagueToRemove) return;
    setRemovingLeague(true);
    try {
      await detachTeamFromSeason(leagueToRemove.league_id, leagueToRemove.season_id);
      setLeagueToRemove(null);
      setMessage({ text: t('mtRemovedLeague'), type: 'success' });
    } catch (error) {
      setLeagueToRemove(null);
      setMessage({ text: getApiErrorMessage(error, t('mtRemoveLeagueError')), type: 'error' });
    } finally {
      setRemovingLeague(false);
    }
  };

  const reloadApplications = async () => {
    const appsResponse = await authorizedPost(dispatch, API_URLS.getAdminTeamApplications, {
      id: Number(teamId),
    });
    const apps = extractApplications(appsResponse.data)
      .slice()
      .sort((a, b) => (Number(a.priority) || 0) - (Number(b.priority) || 0));
    setApplications(apps);
    setSelectedApplicationId((prev) => {
      if (apps.some((app) => String(app.application_id || app.id) === String(prev))) {
        return prev;
      }
      return apps[0] ? String(apps[0].application_id || apps[0].id || '') : '';
    });
  };

  const handleRemovePlayer = async () => {
    if (!playerToRemove || !selectedApplicationId) return;
    setRemovingPlayer(true);
    setMessage({ text: '', type: '' });
    try {
      await authorizedPost(dispatch, API_URLS.changePlayerApplication, {
        id: Number(playerToRemove.id || playerToRemove.player_id),
        player_id: Number(playerToRemove.id || playerToRemove.player_id),
        action: 'remove',
        team_id: Number(teamId),
        application_id: Number(selectedApplicationId),
      });
      setPlayerToRemove(null);
      await reloadApplications();
      setMessage({ text: t('mtRemovedPlayer'), type: 'success' });
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('mtRemovePlayerError')), type: 'error' });
    } finally {
      setRemovingPlayer(false);
    }
  };

  const openLeagueSeason = async (item) => {
    try {
      let countryId = item.country_id;
      if (!countryId) {
        const response = await authorizedPost(dispatch, API_URLS.getAdminLeague, {
          id: Number(item.league_id),
        });
        countryId = extractEntity(response.data).country_id;
      }
      if (!countryId) return;
      history.push(`/tournaments/${countryId}/league/${item.league_id}/season/${item.season_id}`);
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('stLoadError')), type: 'error' });
    }
  };

  const takenSeasonIds = new Set(
    leagues
      .filter((item) => Number(item.league_id) === Number(addLeagueId))
      .map((item) => Number(item.season_id)),
  );
  const availableAddSeasons = addSeasons.filter((item) => (
    !takenSeasonIds.has(Number(item.id ?? item.season_id))
  ));

  const teamForm = (
    <div className="tr-league-form">
      <div className="st-field">
        <label className="st-check">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(event) => updateField('enabled', event.target.checked)}
          />
          {t('stShowOnSite')}
        </label>
      </div>

      <div className="tr-curator-main">
        <div className="st-field tr-curator-photo">
          <label>{t('stPhoto')}</label>
          <AdminPhotoUpload
            preview={photoPreview}
            error={photoError}
            disabled={saving}
            enableCrop={false}
            onFileChange={(file, error, preview) => {
              if (error) {
                setPhotoError(error);
                return;
              }
              setPhotoError('');
              setPhotoFile(file);
              setPhotoPreview(preview);
            }}
          />
        </div>

        <div className="tm-edit-names">
          <div className="st-field">
            <label>{t('tmName')} <span className="st-required">*</span></label>
            <input
              className={`st-input ${errors.name ? 'error' : ''}`}
              value={form.name}
              placeholder={t('tmNamePh')}
              onChange={(event) => updateField('name', event.target.value)}
            />
            {errors.name ? <span className="st-field-error">{errors.name}</span> : null}
          </div>

          <div className="st-field">
            <label>{t('tmShortName')}</label>
            <input
              className="st-input"
              value={form.short_name}
              onChange={(event) => updateField('short_name', event.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="st-field">
        <label>{t('championship')} <span className="st-required">*</span></label>
        <select
          className={`st-select ${errors.country_id ? 'error' : ''}`}
          value={form.country_id}
          onChange={(event) => updateField('country_id', event.target.value)}
        >
          <option value="">{t('trCountryPlaceholder')}</option>
          {countries.map((country) => (
            <option key={country.id} value={country.id}>{country.name}</option>
          ))}
        </select>
        {errors.country_id ? <span className="st-field-error">{errors.country_id}</span> : null}
      </div>

      {!isNew ? (
        <div className="tm-doubles">
          <div className="tm-doubles-head">
            <span className="tm-doubles-title">{t('tmDoublesTitle')}</span>
            <span className="st-photo-hint">{t('tmDoublesHint')}</span>
          </div>

          {doubles.parent ? (
            <div className="tm-double-row">
              <span className="tm-double-label">{t('tmDoubleParent')}</span>
              <Link className="tm-double-card" to={`/teams_management/${doubles.parent.id}`}>
                <img
                  src={getTeamImageUrl(doubles.parent.image) || defaultTeamLogo}
                  alt=""
                  onError={(event) => { event.currentTarget.src = defaultTeamLogo; }}
                />
                <span>{doubles.parent.name || doubles.parent.short_name || `#${doubles.parent.id}`}</span>
              </Link>
              {canWrite ? (
                <button
                  type="button"
                  className="st-icon-btn danger"
                  title={t('tmDoubleUnlink')}
                  disabled={doublesBusy}
                  onClick={() => unlinkTeamDouble('parent')}
                >
                  <FiX />
                </button>
              ) : null}
            </div>
          ) : null}

          {doubles.reserve ? (
            <div className="tm-double-row">
              <span className="tm-double-label">{t('tmDoubleReserve')}</span>
              <Link className="tm-double-card" to={`/teams_management/${doubles.reserve.id}`}>
                <img
                  src={getTeamImageUrl(doubles.reserve.image) || defaultTeamLogo}
                  alt=""
                  onError={(event) => { event.currentTarget.src = defaultTeamLogo; }}
                />
                <span>{doubles.reserve.name || doubles.reserve.short_name || `#${doubles.reserve.id}`}</span>
              </Link>
              {canWrite ? (
                <button
                  type="button"
                  className="st-icon-btn danger"
                  title={t('tmDoubleUnlink')}
                  disabled={doublesBusy}
                  onClick={() => unlinkTeamDouble('reserve')}
                >
                  <FiX />
                </button>
              ) : null}
            </div>
          ) : null}

          {doubles.chain.length > 1 ? (
            <div className="tm-double-chain">
              {doubles.chain.map((item, index) => (
                <React.Fragment key={item.id}>
                  {index > 0 ? <span className="tm-double-chain-sep">→</span> : null}
                  <Link
                    className={`tm-double-chip${Number(item.id) === Number(teamId) ? ' is-current' : ''}`}
                    to={`/teams_management/${item.id}`}
                  >
                    {item.short_name || item.name || `#${item.id}`}
                  </Link>
                </React.Fragment>
              ))}
            </div>
          ) : null}

          {canWrite && !doubles.parent ? (
            <div className="st-field">
              <label>{t('tmDoublePickParent')}</label>
              <AdminSearchPicker
                type="team"
                scopeChamp
                disabled={doublesBusy}
                placeholder={t('tmDoubleSearchPh')}
                excludeKeys={[
                  `team:${teamId}`,
                  ...(doubles.reserve ? [`team:${doubles.reserve.id}`] : []),
                ]}
                onPick={onPickParent}
              />
            </div>
          ) : null}

          {canWrite && !doubles.reserve ? (
            <div className="st-field">
              <label>{t('tmDoublePickReserve')}</label>
              <AdminSearchPicker
                type="team"
                scopeChamp
                disabled={doublesBusy}
                placeholder={t('tmDoubleSearchPh')}
                excludeKeys={[
                  `team:${teamId}`,
                  ...(doubles.parent ? [`team:${doubles.parent.id}`] : []),
                ]}
                onPick={onPickReserve}
              />
            </div>
          ) : null}
        </div>
      ) : (
        <p className="st-photo-hint">{t('tmDoublesAfterCreate')}</p>
      )}
    </div>
  );

  const selectedApplication = applications.find((app) => (
    String(app.application_id || app.id) === String(selectedApplicationId)
  ));
  const players = extractList(selectedApplication?.players).map((player) => ({
    ...player,
    id: player.id || player.player_id,
  }));
  const query = playerQuery.trim().toLowerCase();
  const visiblePlayers = players
    .filter((item) => {
      if (!query) return true;
      const name = playerDisplayName(item).toLowerCase();
      const family = String(item.family || '').toLowerCase();
      const firstName = String(item.name || '').toLowerCase();
      const fatherName = String(item.father_name || '').toLowerCase();
      const number = String(item.number ?? item.player_number ?? item.num ?? '');
      return query.split(/\s+/).filter(Boolean).every((token) => (
        name.includes(token)
        || family.includes(token)
        || firstName.includes(token)
        || fatherName.includes(token)
        || number.includes(token)
      ));
    })
    .slice()
    .sort((left, right) => {
      const dir = playerSort.dir === 'desc' ? -1 : 1;
      if (playerSort.key === 'number') {
        const leftNumber = Number(left.number ?? left.player_number ?? left.num);
        const rightNumber = Number(right.number ?? right.player_number ?? right.num);
        const leftValue = Number.isFinite(leftNumber) && leftNumber > 0 ? leftNumber : Number.POSITIVE_INFINITY;
        const rightValue = Number.isFinite(rightNumber) && rightNumber > 0 ? rightNumber : Number.POSITIVE_INFINITY;
        return (leftValue - rightValue) * dir;
      }
      if (playerSort.key === 'position') {
        const leftValue = String(left.position || left.short_position || left.amplua || '');
        const rightValue = String(right.position || right.short_position || right.amplua || '');
        return leftValue.localeCompare(rightValue, 'ru', { sensitivity: 'base' }) * dir;
      }
      return playerDisplayName(left).localeCompare(playerDisplayName(right), 'ru', { sensitivity: 'base' }) * dir;
    });

  const selectedApplicationName = selectedApplication
    ? (selectedApplication.application_name || selectedApplication.name || t('mtAppFallback', { id: selectedApplicationId }))
    : t('pdApp');

  const openPlayerCard = (item) => {
    if (!item?.id) return;
    history.push(playerCardPath(item.id, teamId));
  };

  const playerColumns = [
    {
      key: 'name',
      label: t('tmPlayer'),
      className: 'st-cell-name',
      render: (item) => (
        <span className="tr-contact">
          <img
            src={getPlayerImageUrl(item.image) || defaultPlayerImage}
            alt=""
            className="tr-contact-photo"
            onError={(event) => { event.currentTarget.src = defaultPlayerImage; }}
          />
          <span className="tr-contact-text">{playerDisplayName(item) || t('tmUntitled')}</span>
          {isPlayerProfessional(item) ? (
            <span className="cap-pro-badge" title={t('plStatusPro')}>{t('plStatusProShort')}</span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'position',
      label: t('mtPosition'),
      className: 'st-cell-muted',
      render: (item) => item.position || item.short_position || item.amplua || '—',
    },
    {
      key: 'number',
      label: t('tmNumber'),
      className: 'st-cell-priority',
      render: (item) => (
        <>
          <span>{item.number || item.player_number || item.num || '—'}</span>
          {(canWritePlayers || canWrite) ? (
            <button
              type="button"
              className="tm-player-remove st-icon-btn danger"
              title={t('mtRemovePlayerTitle')}
              aria-label={t('mtRemovePlayerTitle')}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setPlayerToRemove(item);
              }}
            >
              <FiX />
            </button>
          ) : null}
        </>
      ),
    },
  ];

  const coachColumns = [
    {
      key: 'name',
      label: t('stdName'),
      className: 'st-cell-name',
      render: (item) => {
        const role = coachRole(item) || '';
        const birthday = toDateInputValue(coachBirthdayValue(item)).split('-').reverse().join('.') || '';
        const meta = [role, birthday].filter(Boolean).join(' · ');
        return (
          <span className="tm-official-main">
            <span className="tm-official-name">{coachDisplayName(item) || '—'}</span>
            {meta ? <small className="tm-official-meta">{meta}</small> : null}
          </span>
        );
      },
    },
    {
      key: 'position',
      label: t('trPosition'),
      className: 'st-cell-muted st-cell-center tm-official-role',
      render: (item) => coachRole(item) || '—',
    },
    {
      key: 'birthday',
      label: t('tmBirthDate'),
      className: 'st-cell-muted st-cell-date tm-official-birthday',
      render: (item) => toDateInputValue(coachBirthdayValue(item)).split('-').reverse().join('.') || '—',
    },
  ];

  const leagueColumns = [
    {
      key: 'league',
      label: t('league'),
      className: 'st-cell-name',
      render: (item) => {
        const season = item.season_name || (item.season_id ? t('atlSeasonFallback', { id: item.season_id }) : '');
        const fallback = defaultLeagueImage();
        return (
          <span className="tr-contact tm-league-main">
            <img
              src={item.league_image ? getTournamentImageUrl(item.league_image) : fallback}
              alt=""
              className="tr-contact-photo"
              onError={(event) => { event.currentTarget.src = fallback; }}
            />
            <span className="tm-league-text">
              <span className="tm-league-name">{item.league_name || t('tmUntitled')}</span>
              {season ? <small className="tm-league-meta">{season}</small> : null}
            </span>
          </span>
        );
      },
    },
    {
      key: 'season',
      label: t('stSeason'),
      className: 'st-cell-muted tm-league-season',
      render: (item) => item.season_name || t('atlSeasonFallback', { id: item.season_id }),
    },
    {
      key: 'actions',
      label: '',
      className: 'st-cell-actions',
      sortable: false,
      render: (item) => (
        canWrite ? (
        <button
          type="button"
          className="st-icon-btn danger"
          title={t('mtRemoveLeagueTitle')}
          onClick={(event) => {
            event.stopPropagation();
            setLeagueToRemove(item);
          }}
        >
          <FiTrash2 />
        </button>
        ) : null
      ),
    },
  ];

  const crumbs = [
    { label: t('navTeams'), to: '/teams_management' },
    { label: isNew ? t('tmNewTeam') : (form.name.trim() || t('tmEditTeam')) },
  ];

  const saveButton = canWrite ? (
    <button
      type="button"
      className="st-btn st-btn-primary"
      disabled={loading || saving}
      onClick={handleSave}
    >
      <FiSave /> {saving ? t('trLoading') : t('stdSaveChanges')}
    </button>
  ) : null;

  if (loading) {
    return (
      <div className="layoutWrapper st-layout">
        <div className="matchCenterWrapper st-page">
          <div className="matchCenterHeader">
            <div className="st-header">
              <StadiumCrumbs items={crumbs} />
            </div>
          </div>
          <div className="st-loading">
            <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
            <p>{t('trLoading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (isNew) {
    return (
      <div className="layoutWrapper st-layout">
        <div className="matchCenterWrapper st-page">
          <div className="matchCenterHeader">
            <div className="st-header">
              <StadiumCrumbs items={crumbs} />
            </div>
          </div>

          {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

          <div className="st-scroll">
            <div className="st-content">
              <div className="st-block">
                <h2 className="st-block-header">{t('tmNewTeam')}</h2>
                {teamForm}
              </div>
            </div>
          </div>

          <div className="st-actions">
            {saveButton}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page tm-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs items={crumbs} />
          </div>
        </div>

        {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

        <div className="st-scroll">
          <div className="dq-split tm-split">
            <section className="st-block dq-panel tm-form-panel">
              <h2 className="st-block-header">{t('tmEditTeam')}</h2>
              <div className="dq-panel-body">
                {teamForm}
              </div>
              <div className="st-actions">
                {saveButton}
              </div>
            </section>

            <section className="st-block dq-panel">
              <div className="tm-block-head">
                <h2 className="st-block-header">{t('tmPlayersBlock')}</h2>
                <div className="tm-app-switcher">
                  <label htmlFor="tm-application-select">{t('mtPickApp')}</label>
                  <select
                    id="tm-application-select"
                    className="st-select"
                    value={selectedApplicationId}
                    disabled={!applications.length}
                    onChange={(event) => {
                      setSelectedApplicationId(event.target.value);
                      setPlayerQuery('');
                    }}
                  >
                    {applications.length ? applications.map((app) => {
                      const appId = app.application_id || app.id;
                      const playerCount = extractList(app.players).length;
                      return (
                        <option key={appId} value={String(appId)}>
                          {t('mtAppPlayers', {
                            name: app.application_name || app.name || t('mtAppFallback', { id: appId }),
                            count: playerCount,
                          })}
                        </option>
                      );
                    }) : (
                      <option value="">{t('mtNoApps')}</option>
                    )}
                  </select>
                </div>
              </div>
              <div className="tm-player-search">
                <FiSearch />
                <input
                  className="st-input"
                  value={playerQuery}
                  placeholder={t('tmPlayerSearchPh')}
                  onChange={(event) => setPlayerQuery(event.target.value)}
                />
              </div>
              <div className="dq-panel-body">
                <ReorderableTable
                  tableClassName="st-table-squad"
                  columns={playerColumns}
                  items={visiblePlayers}
                  getRowKey={(item, index) => item.id || index}
                  reorderMode={false}
                  sort={playerSort}
                  onSort={setPlayerSort}
                  onRowClick={openPlayerCard}
                  empty={(
                    <div className="st-empty">
                      <p>{!applications.length ? t('mtNoApps') : (query ? t('tmNoPlayerSearch') : t('mtNoPlayers'))}</p>
                    </div>
                  )}
                />
              </div>
              <div className="st-actions">
                {canWritePlayers ? (
                <Link to={playerNewPath(teamId)} className="st-btn st-btn-primary">
                  <FiPlus /> {t('tmAddPlayer')}
                </Link>
                ) : null}
              </div>
            </section>

            <div className="dq-stack tm-side-stack">
              <section className="st-block dq-panel">
                <h2 className="st-block-header">{t('tmOfficials')}</h2>
                <div className="dq-panel-body">
                  <ReorderableTable
                    tableClassName="st-table-officials"
                    columns={coachColumns}
                    items={coaches}
                    getRowKey={(item) => item.id}
                    reorderMode={false}
                    getRowHref={(item) => (item.id ? `/teams_management/${teamId}/coach/${item.id}` : null)}
                    empty={<div className="st-empty"><p>{t('tmEmptyOfficials')}</p></div>}
                  />
                </div>
                <div className="st-actions">
                  {canWrite ? (
                  <Link to={`/teams_management/${teamId}/coach/new`} className="st-btn st-btn-primary">
                    <FiPlus /> {t('tmAddOfficial')}
                  </Link>
                  ) : null}
                </div>
              </section>

              <section className="st-block dq-panel">
                <h2 className="st-block-header">{t('mtLeagues')}</h2>
                <div className="dq-panel-body">
                  <ReorderableTable
                    tableClassName="st-table-leagues"
                    columns={leagueColumns}
                    items={leagues}
                    getRowKey={(item, index) => `${item.league_id}_${item.season_id}_${index}`}
                    reorderMode={false}
                    onRowClick={openLeagueSeason}
                    empty={<div className="st-empty"><p>{t('mtNoLeagues')}</p></div>}
                  />
                </div>
                <div className="st-actions">
                  {canWrite ? (
                  <button
                    type="button"
                    className="st-btn st-btn-primary"
                    onClick={openAddLeague}
                  >
                    <FiPlus /> {t('mtAddToLeague')}
                  </button>
                  ) : null}
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>

      <CustomModal
        styles={{ width: 460, height: 'auto', paddingLeft: 32, paddingRight: 32 }}
        visible={addLeagueVisible}
        closeModal={closeAddLeague}
      >
        <div className="st-delete-modal tm-add-league-modal">
          <div className="customModalHeader">{t('atlTitle')}</div>
          {addLeagueError ? <div className={`st-message error`}>{addLeagueError}</div> : null}
          <div className="st-field">
            <label>{t('trCountry')} <span className="st-required">*</span></label>
            <select
              className="st-select"
              value={addCountryId}
              disabled={addLeagueLoading || addingLeague}
              onChange={(event) => handleAddCountryChange(event.target.value)}
            >
              <option value="">{t('trCountryPlaceholder')}</option>
              {countries.map((country) => (
                <option key={country.id} value={country.id}>{country.name}</option>
              ))}
            </select>
          </div>
          <div className="st-field">
            <label>{t('league')} <span className="st-required">*</span></label>
            <select
              className="st-select"
              value={addLeagueId}
              disabled={addLeagueLoading || addingLeague || !addCountryId}
              onChange={(event) => handleAddLeagueChange(event.target.value)}
            >
              <option value="">{t('leaguePlaceholder')}</option>
              {addCountryLeagues.map((item) => {
                const leagueId = item.id || item.league_id;
                return (
                  <option key={leagueId} value={leagueId}>{item.name || item.league_name}</option>
                );
              })}
            </select>
          </div>
          <div className="st-field">
            <label>{t('stSeason')} <span className="st-required">*</span></label>
            <select
              className="st-select"
              value={addSeasonId}
              disabled={addLeagueLoading || addingLeague || !addLeagueId}
              onChange={(event) => setAddSeasonId(event.target.value)}
            >
              <option value="">{t('stSeasonPlaceholder')}</option>
              {availableAddSeasons.map((item) => {
                const seasonId = item.id || item.season_id;
                return (
                  <option key={seasonId} value={seasonId}>{item.name || item.season_name || t('atlSeasonFallback', { id: seasonId })}</option>
                );
              })}
            </select>
            {addLeagueId && !addLeagueLoading && addSeasons.length > 0 && !availableAddSeasons.length ? (
              <span className="st-field-error">{t('atlNoSeasons')}</span>
            ) : null}
          </div>
          <div className="st-delete-actions">
            <button type="button" className="btn-modal btn-secondary-modal" disabled={addingLeague} onClick={closeAddLeague}>
              {t('cancel')}
            </button>
            <button
              type="button"
              className="btn-modal btn-primary-modal"
              disabled={addingLeague || addLeagueLoading || !addLeagueId || !addSeasonId}
              onClick={handleAddLeague}
            >
              {addingLeague ? t('trLoading') : t('atlAdd')}
            </button>
          </div>
        </div>
      </CustomModal>

      <CustomModal
        styles={{ width: 420, height: 'auto', paddingLeft: 32, paddingRight: 32 }}
        visible={Boolean(leagueToRemove)}
        closeModal={() => !removingLeague && setLeagueToRemove(null)}
      >
        <div className="st-delete-modal">
          <div className="customModalHeader">{t('mtRemoveLeagueTitle')}</div>
          <p>
            {t('mtRemoveLeagueConfirm', {
              league: leagueToRemove?.league_name || t('league'),
              season: leagueToRemove?.season_name || t('stSeason'),
            })}
          </p>
          <div className="st-delete-actions">
            <button
              type="button"
              className="btn-modal btn-secondary-modal"
              disabled={removingLeague}
              onClick={() => setLeagueToRemove(null)}
            >
              {t('cancel')}
            </button>
            <button
              type="button"
              className="btn-modal btn-danger-modal"
              disabled={removingLeague}
              onClick={handleRemoveLeague}
            >
              {removingLeague ? t('trLoading') : t('delete')}
            </button>
          </div>
        </div>
      </CustomModal>

      <CustomModal
        styles={{ width: 420, height: 'auto', paddingLeft: 32, paddingRight: 32 }}
        visible={Boolean(playerToRemove)}
        closeModal={() => !removingPlayer && setPlayerToRemove(null)}
      >
        <div className="st-delete-modal">
          <div className="customModalHeader">{t('mtRemovePlayerTitle')}</div>
          <p>
            {t('mtRemovePlayerConfirm', {
              name: playerDisplayName(playerToRemove) || t('tmPlayer'),
              app: selectedApplicationName,
            })}
          </p>
          <div className="st-delete-actions">
            <button
              type="button"
              className="btn-modal btn-secondary-modal"
              disabled={removingPlayer}
              onClick={() => setPlayerToRemove(null)}
            >
              {t('cancel')}
            </button>
            <button
              type="button"
              className="btn-modal btn-danger-modal"
              disabled={removingPlayer}
              onClick={handleRemovePlayer}
            >
              {removingPlayer ? t('trLoading') : t('delete')}
            </button>
          </div>
        </div>
      </CustomModal>

      <CustomModal
        styles={{ width: 460, height: 'auto', paddingLeft: 32, paddingRight: 32 }}
        visible={Boolean(doubleReplace)}
        closeModal={() => !doublesBusy && setDoubleReplace(null)}
      >
        <div className="st-delete-modal">
          <div className="customModalHeader">{t('tmDoubleConflictTitle')}</div>
          <p>{t('tmDoubleConflictText')}</p>
          {doubleReplace?.conflictsText ? (
            <pre className="tm-double-conflicts">{doubleReplace.conflictsText}</pre>
          ) : null}
          <div className="st-delete-actions">
            <button
              type="button"
              className="btn-modal btn-secondary-modal"
              disabled={doublesBusy}
              onClick={() => setDoubleReplace(null)}
            >
              {t('cancel')}
            </button>
            <button
              type="button"
              className="btn-modal btn-danger-modal"
              disabled={doublesBusy}
              onClick={() => linkTeamDouble({
                mainId: doubleReplace.mainId,
                doubleId: doubleReplace.doubleId,
                replace: true,
              })}
            >
              {doublesBusy ? t('trLoading') : t('tmDoubleReplace')}
            </button>
          </div>
        </div>
      </CustomModal>
    </div>
  );
}
